/**
 * 画像タイルによるソースプロバイダ（FR-SRC-04〜05・FR-ZOOM-02）。
 *
 * 規律（破ると破綻する）:
 *   - 下地（全面）は必ず view 画像 1 枚から作る。タイルで合成すると 1 ページの表示に
 *     数十リクエストかかり、ページ送りが成立しない。
 *   - タイルは拡大（領域ラスタ）のためだけに使う。可視領域が交差するタイルだけを取る。
 *   - タイルを持たないページ（寸法閾値以下）は full 画像 1 枚で拡大に足りる。
 *
 * ラスタは HTMLCanvasElement で返す（pdf プロバイダと同じ形）。three.js は ImageBitmap を
 * 与えると環境によって上下反転するため、canvas に統一して事故を構造的に避ける。
 */
import { ViewerError } from '../../core/errors';
import type { BookManifest, ManifestSession, PageInfo, PageUrls } from '../../core/manifest';
import { FULL_PAGE, isFullPage } from '../../core/source';
import type { PageRaster, PageRasterRequest, PageRegion, SourceProvider } from '../../core/source';
import { maxWidthFor } from '../canvas-limits';
import { signedFetch } from '../signed-fetch';
import { grid, levelForWidth, levelSize, pyramid, tileRange, tileRect } from './dzi';
import type { Pyramid } from './dzi';
import { fullUrl, pickFormat, thumbUrl, tileUrl, viewUrl } from './urls';

/**
 * タイルの同時取得数。配信は HTTP/2 多重化が前提なので、6（HTTP/1.1 時代の
 * 同時接続数）に抑える理由はない。モバイル回線では往復遅延が支配的なため、
 * 並列度を上げるほど拡大の待ち時間が縮む。
 */
const TILE_CONCURRENCY = 12;

/** この環境が WebP を復号できるか（decode を 1 度だけ試す）。 */
async function detectWebp(): Promise<boolean> {
  const pixel =
    'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==';
  try {
    const image = new Image();
    image.src = pixel;
    await image.decode();
    return image.width > 0;
  } catch {
    return false;
  }
}

export class ImageTilesProvider implements SourceProvider {
  private session: ManifestSession | null = null;
  private format = 'jpg';

  async open(session: ManifestSession): Promise<void> {
    this.session = session;
    const manifest = session.current;
    if (!manifest.urls.pages || !manifest.imaging) {
      throw new ViewerError('E_MANIFEST_MISMATCH', '画像配信モードの成果物がマニフェストに無い');
    }
    this.format = pickFormat(manifest.imaging.formats, await detectWebp());
  }

  get pageCount(): number {
    return this.session?.current.pageCount ?? 0;
  }

  /** 原本が供給できる全面換算の上限（拡大上限 = 生成解像度）。 */
  maxEffectiveWidth(pageNo: number): number {
    const info = this.pageInfo(pageNo);
    return info.image?.width ?? 1;
  }

  maxRasterWidth(pageNo: number): number {
    const info = this.pageInfo(pageNo);
    return maxWidthFor(info.height / info.width);
  }

  async renderPage(request: PageRasterRequest): Promise<PageRaster> {
    const { pageNo, targetWidth, signal } = request;
    const region = request.region ?? FULL_PAGE;
    const info = this.pageInfo(pageNo);
    const tiles = info.tiles;

    // タイルを使うのは「領域 × タイルあり」のときだけ。
    // 下地（全面）をタイルで合成すると 1 ページに数十リクエストかかり、ページ送りが成立しない。
    if (!isFullPage(region) && tiles && info.image) {
      return this.composeFromTiles(pageNo, region, targetWidth, info, tiles, signal);
    }

    // それ以外は単一画像から切り出す（下地 = view / 拡大でタイルが無いページ = full）
    const useFull = !isFullPage(region) || this.exceedsView(targetWidth, info);
    const bitmap = await this.load(
      (manifest) =>
        useFull && tiles === undefined
          ? fullUrl(this.pages(manifest), pageNo, this.format)
          : viewUrl(this.pages(manifest), pageNo, this.format),
      signal,
    );
    const raster = this.compose(pageNo, region, targetWidth, info);
    const ctx = (raster.bitmap as HTMLCanvasElement).getContext('2d');
    ctx?.drawImage(
      bitmap,
      region.x * bitmap.width,
      region.y * bitmap.height,
      region.w * bitmap.width,
      region.h * bitmap.height,
      0,
      0,
      raster.width,
      raster.height,
    );
    bitmap.close();
    return raster;
  }

  async dispose(): Promise<void> {
    this.session = null;
  }

  /** サムネイル（サーバ生成）。マニフェストが持たないブックでは呼ばれない。 */
  thumbUrlFor(pageNo: number): string {
    return thumbUrl(this.pages(this.requireSession().current), pageNo);
  }

  // -- 内部 ----------------------------------------------------------------

  private requireSession(): ManifestSession {
    if (!this.session) throw new ViewerError('E_STATE', 'ソースが未オープン');
    return this.session;
  }

  private pages(manifest: BookManifest): PageUrls {
    const urls = manifest.urls.pages;
    if (!urls) throw new ViewerError('E_ACCESS_DENIED', 'ページ配信 URL が許可されていない');
    return urls;
  }

  private pageInfo(pageNo: number): PageInfo {
    const info = this.session?.current.pages[pageNo - 1];
    if (!info) throw new ViewerError('E_STATE', `ページ ${pageNo} はマニフェストに存在しない`);
    return info;
  }

  /** 下地（view）の解像度を超える要求か。超えるなら full 画像を使う。 */
  private exceedsView(targetWidth: number, info: PageInfo): boolean {
    const viewEdge = this.session?.current.imaging?.viewEdge ?? 0;
    const image = info.image;
    if (!image || viewEdge <= 0) return false;
    const longest = Math.max(image.width, image.height);
    const viewWidth = longest <= viewEdge ? image.width : (image.width * viewEdge) / longest;
    return targetWidth > viewWidth;
  }

  private async load(
    build: (manifest: BookManifest) => string,
    signal: AbortSignal | undefined,
  ): Promise<ImageBitmap> {
    const session = this.requireSession();
    const response = await signedFetch(session, build, { signal });
    if (!response.ok) {
      throw new ViewerError('E_HTTP', `画像を取得できない（HTTP ${response.status}）`);
    }
    const blob = await response.blob();
    signal?.throwIfAborted();
    return createImageBitmap(blob);
  }

  /** 領域ぶんの出力キャンバスを用意する（描き込みは呼び出し側）。 */
  private compose(
    pageNo: number,
    region: PageRegion,
    targetWidth: number,
    info: PageInfo,
  ): PageRaster {
    const aspect = info.height / info.width;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(targetWidth));
    canvas.height = Math.max(1, Math.round((targetWidth * aspect * region.h) / region.w));
    if (!canvas.getContext('2d')) {
      throw new ViewerError('E_CANVAS', '2D コンテキストを取得できない');
    }

    return {
      pageNo,
      bitmap: canvas,
      width: canvas.width,
      height: canvas.height,
      region,
      close: () => {
        canvas.width = 0;
        canvas.height = 0;
      },
    };
  }

  private async composeFromTiles(
    pageNo: number,
    region: PageRegion,
    targetWidth: number,
    info: PageInfo,
    tiles: NonNullable<PageInfo['tiles']>,
    signal: AbortSignal | undefined,
  ): Promise<PageRaster> {
    const imaging = this.requireSession().current.imaging;
    const image = info.image;
    if (!imaging || !image) throw new ViewerError('E_STATE', '画像メタが無い');

    const p: Pyramid = pyramid({
      width: image.width,
      height: image.height,
      tileSize: imaging.tileSize,
      overlap: imaging.overlap,
      maxLevel: tiles.maxLevel,
      minLevel: tiles.minLevel,
    });

    // 領域の目標幅 → ページ全面換算の幅 → 解像度レベル
    const level = levelForWidth(p, targetWidth / region.w);
    const size = levelSize(p, level);
    const range = tileRange(p, level, region);
    const { cols, rows } = grid(p, level);

    const raster = this.compose(pageNo, region, targetWidth, info);
    const ctx = (raster.bitmap as HTMLCanvasElement).getContext('2d');
    if (!ctx) throw new ViewerError('E_CANVAS', '2D コンテキストを取得できない');

    // レベル座標 → 出力キャンバス座標
    const scale = raster.width / (region.w * size.width);
    const originX = region.x * size.width;
    const originY = region.y * size.height;

    const jobs: Array<{ col: number; row: number }> = [];
    for (let col = range.col0; col <= range.col1 && col < cols; col += 1) {
      for (let row = range.row0; row <= range.row1 && row < rows; row += 1) {
        jobs.push({ col, row });
      }
    }

    let next = 0;
    const workers = Array.from({ length: Math.min(TILE_CONCURRENCY, jobs.length) }, async () => {
      for (let index = next++; index < jobs.length; index = next++) {
        const job = jobs[index];
        if (!job) break;
        const { col, row } = job;
        const rect = tileRect(p, level, col, row);
        const bitmap = await this.load(
          (manifest) => tileUrl(this.pages(manifest), pageNo, level, col, row, this.format),
          signal,
        );
        signal?.throwIfAborted();
        // overlap ぶんは隣のタイルと同じ画素なので、そのまま重ねて描いてよい
        ctx.drawImage(
          bitmap,
          (rect.x - originX) * scale,
          (rect.y - originY) * scale,
          rect.width * scale,
          rect.height * scale,
        );
        bitmap.close();
      }
    });

    try {
      await Promise.all(workers);
    } catch (error) {
      raster.close();
      throw error;
    }
    return raster;
  }
}
